import { DatasheetEnsureOutcome, DatasheetStateReason } from '@cadstrom/cadstrom-shared';

import type { AlertColor } from '@mui/material/Alert';

export enum ReplaceDatasheetReason {
	Replacing = 'Replacing',
	ExplicitlyEmpty = 'ExplicitlyEmpty',
	ErrorRetrievingFile = 'ErrorRetrievingFile',
	InvalidPdf = 'InvalidPdf',
}

export const replaceDatasheetDialogBgProps = {
	className: 'absolute inset-0 [--bg-gradient-opacity:0.3] light:[--bg-gradient-opacity:0.08]',
	style: {
		backgroundImage:
			'radial-gradient(ellipse farthest-side at center, rgb(0 0 0 / var(--bg-gradient-opacity)) 50%, transparent 100%)',
	},
};

/** Permission to replace a package's datasheet, and where to lay the prompt that offers it. */
export interface AllowReplaceDatasheet {
	/** The package whose datasheet may be replaced. */
	pkgId: string;
	/**
	 * The element the prompt and its backdrop cover, defaulting to the datasheet viewer's own outermost
	 * element. An element supplied here must establish a containing block for absolute positioning, or
	 * the prompt will be laid against whichever of its ancestors does.
	 */
	container?: HTMLElement | null;
}

/** Where a package's datasheet search stood when the request for it returned. */
export interface DatasheetSearchResult {
	/** True when the search was still running when the request stopped waiting. */
	running: boolean;
	/** How the search ended; null while it runs. */
	outcome: DatasheetEnsureOutcome | null;
	/** Why the package has no datasheet, when the search ended without one. */
	reason: DatasheetStateReason | null;
	/** ISO-8601; when the package is next searched, when the search ended without a datasheet. */
	nextAttemptAt: string | null;
}

/** How a datasheet search's result reads to the operator. */
export interface DatasheetSearchMessage {
	severity: AlertColor;
	text: string;
}

/** Phrases for why a search ended without a datasheet. */
const NOT_FOUND_REASON_TEXT: Partial<Record<DatasheetStateReason, string>> = {
	[DatasheetStateReason.AgentNotFound]: 'the agent search found nothing',
	[DatasheetStateReason.AllSourcesFailed]: 'every source failed',
	[DatasheetStateReason.NoSources]: 'the package has no part number or link to search with',
	[DatasheetStateReason.AllCandidatesRejected]: 'every file found was rejected as the wrong document',
};

/**
 * Describes a datasheet search's result for the operator who asked for it.
 *
 * @param result - Where the search stood when the request returned.
 * @param formatDate - Formats an ISO-8601 instant for display.
 * @returns The message to show, with its severity.
 */
export function describeDatasheetSearch(
	result: DatasheetSearchResult,
	formatDate: (iso: string) => string = (iso) => new Date(iso).toLocaleString(),
): DatasheetSearchMessage {
	if (result.running) {
		return {
			severity: 'info',
			text: 'Still searching. The search carries on in the background; check back in a few minutes.',
		};
	}
	switch (result.outcome) {
		case DatasheetEnsureOutcome.Attached: {
			return { severity: 'success', text: 'Found a datasheet and attached it.' };
		}
		case DatasheetEnsureOutcome.AlreadyFound: {
			return {
				severity: 'info',
				text: 'This package already has a datasheet, so no search ran. Upload a file or give a URL to replace it.',
			};
		}
		case DatasheetEnsureOutcome.NotFound: {
			const reason = result.reason === null ? undefined : NOT_FOUND_REASON_TEXT[result.reason];
			const retry =
				result.nextAttemptAt === null ? '' : ` It will be searched again after ${formatDate(result.nextAttemptAt)}.`;
			return {
				severity: 'warning',
				text: `No datasheet found${reason === undefined ? '' : `: ${reason}`}.${retry}`,
			};
		}
		case DatasheetEnsureOutcome.NotClaimed: {
			return {
				severity: 'info',
				text: 'No search ran: another search for this package is under way, or the package needs no datasheet.',
			};
		}
		case DatasheetEnsureOutcome.CandidateFound: {
			return {
				severity: 'info',
				text: 'A possible replacement was found and is waiting to be checked; nothing was attached yet.',
			};
		}
		case DatasheetEnsureOutcome.Failed:
		case null: {
			return { severity: 'error', text: 'The search failed. Try again, or upload a file or give a URL.' };
		}
	}
}
